import React from "react";
import { OrderView } from "@/api";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Download,
  CheckCircle2,
  Clock3,
  AlertCircle,
} from "lucide-react";
import { printTaxInvoice } from "./printInvoice";
import PaymentTransactionsSection from "./OrderPaymentTransactionTableView";

export interface OrderPaymentViewProps {
  data: OrderView;
  expanded?: boolean;
}

export default function OrderPaymentView({
  data,
  expanded = false,
}: OrderPaymentViewProps) {
  const navigate = useNavigate();

  const { invoice, items = [] } = data;
  const commercial = invoice?.commercial;
  const paymentSchedules = invoice?.payment_schedules ?? [];

  const paymentStatus = invoice?.payment_status ?? "pending";

  const invoiceAmount = commercial?.final_amount ?? "0";
  const paidAmount = invoice?.total_paid_amount ?? "0";
  const outstandingAmount =
    invoice?.total_outstanding_amount ?? "0";

  const qrCode =
    "/assets/images/payment/rzp_qr_code.png";

  return (
    <section
      className={[
        "mx-auto w-full overflow-hidden rounded-xl border bg-white shadow-sm",
        expanded ? "max-w-7xl" : "max-w-5xl",
      ].join(" ")}
    >
      {/* ============================================================
          FIRST ROW
      ============================================================ */}
      <header className="flex items-center justify-between gap-4 border-b px-6 py-4">
        <h1 className="text-lg font-semibold text-gray-900">
          Order Payment View
        </h1>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition hover:bg-gray-50"
          >
            <ArrowLeft size={16} />
            Back
          </button>
          <button
            type="button"
            title="Download Tax Invoice"
            className="inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 cursor-pointer"
            onClick={() => navigate(`/home/orders/receipt/${data.id}`)}
          >
            <Download size={16} />
            Tax Invoice/Receipt
          </button>

        </div>
      </header>

      <div className="p-6">
        {/* ============================================================
            SECOND ROW
        ============================================================ */}
        <div className="flex items-center justify-between gap-4 border-b pb-5">
          <p className="text-sm font-semibold text-gray-900">
            Invoice # {invoice?.invoice_number || "—"} dated{" "}
            {formatDate(invoice?.invoice_date)}
          </p>

          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500">Payment Status:</span>
            <StatusBadge status={paymentStatus} />
          </div>
        </div>

        {/* ============================================================
            PAYMENT + AMOUNT SUMMARY
        ============================================================ */}
        <div className="grid gap-6 py-6 lg:grid-cols-[1fr_320px]">
          {/* Payment */}
          <div className="rounded-xl border bg-gray-50 p-5">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">
                Pay this Invoice
              </h2>

              <p className="mt-1 text-sm text-gray-500">
                Scan the QR code using your preferred payment app.
              </p>
            </div>

            <div className="mt-5 flex flex-col items-center">
              <div className="rounded-xl border bg-white p-3 shadow-sm">
                <img
                  src={qrCode}
                  alt="Invoice payment QR code"
                  className="h-52 w-52 object-contain"
                />
              </div>

              <p className="mt-3 text-xs text-gray-500">
                Invoice #{invoice?.invoice_number || "—"}
              </p>
            </div>

            <div className="mt-5 border-t pt-4 text-center">
              <p className="text-xs text-gray-500">
                Amount Payable
              </p>

              <p className="mt-1 text-2xl font-bold text-gray-900">
                {money(outstandingAmount)}
              </p>

              {Number(paidAmount) > 0 && (
                <p className="mt-1 text-xs text-gray-500">
                  Paid: {money(paidAmount)}
                </p>
              )}
            </div>
          </div>

          {/* Commercial Summary */}
          <div className="rounded-xl border p-5">
            <h2 className="mb-4 text-sm font-semibold text-gray-900">
              Amount Summary
            </h2>

            <div className="space-y-3 text-sm">
              <SummaryRow
                label="Subtotal"
                value={commercial?.subtotal_amount}
              />

              <SummaryRow
                label="Item Discount"
                value={commercial?.items_discount_amount}
              />

              <SummaryRow
                label="Commercial Discount"
                value={
                  commercial?.commercial_discount_amount
                }
              />

              <SummaryRow
                label="Tax"
                value={commercial?.tax_amount}
              />

              <SummaryRow
                label="Round Off"
                value={commercial?.round_off_amount}
              />

              <div className="border-t pt-3">
                <SummaryRow
                  label="Invoice Amount"
                  value={invoiceAmount}
                  strong
                />
              </div>

              <div className="border-t pt-3">
                <SummaryRow
                  label="Paid"
                  value={paidAmount}
                />

                <div className="mt-2">
                  <SummaryRow
                    label="Outstanding"
                    value={outstandingAmount}
                    strong
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ============================================================
            ITEMS
        ============================================================ */}
        <div className="border-t pt-6">
          <SectionTitle>Invoice Items</SectionTitle>

          {items.length === 0 ? (
            <EmptyState message="No invoice items available." />
          ) : (
            <div className="mt-3 overflow-x-auto rounded-lg border">
              <table className="w-full min-w-[900px] text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <TableHeader align="left">
                      Service
                    </TableHeader>

                    <TableHeader align="right">
                      Qty
                    </TableHeader>

                    <TableHeader align="right">
                      Unit Price
                    </TableHeader>

                    <TableHeader align="right">
                      Discount
                    </TableHeader>

                    <TableHeader align="right">
                      Taxable
                    </TableHeader>

                    <TableHeader align="right">
                      GST
                    </TableHeader>

                    <TableHeader align="right">
                      Total
                    </TableHeader>
                  </tr>
                </thead>

                <tbody>
                  {items.map((item) => {
                    const taxAmount =
                      Number(item.cgst_amount ?? 0) +
                      Number(item.sgst_amount ?? 0) +
                      Number(item.igst_amount ?? 0) +
                      Number(item.cess_amount ?? 0);

                    return (
                      <tr
                        key={item.id}
                        className="border-b last:border-0"
                      >
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900">
                            {item.description ||
                              item.program_code ||
                              `Application #${item.application_id}`}
                          </p>

                          {item.hsn_sac && (
                            <p className="mt-1 text-xs text-gray-500">
                              HSN/SAC: {item.hsn_sac}
                            </p>
                          )}

                          {item.application_id && (
                            <p className="mt-1 text-xs text-gray-400">
                              Application #{item.application_id}
                            </p>
                          )}
                        </td>

                        <td className="px-4 py-3 text-right">
                          {item.quantity}
                        </td>

                        <td className="px-4 py-3 text-right">
                          {money(item.unit_price)}
                        </td>

                        <td className="px-4 py-3 text-right">
                          <div>
                            {formatPercentage(
                              item.discount_percentage
                            )}
                          </div>

                          <div className="mt-1 text-xs text-gray-500">
                            {money(item.discount_amount)}
                          </div>
                        </td>

                        <td className="px-4 py-3 text-right">
                          {money(item.taxable_amount)}
                        </td>

                        <td className="px-4 py-3 text-right">
                          <div>
                            {formatPercentage(
                              item.tax_percentage
                            )}
                          </div>

                          <div className="mt-1 text-xs text-gray-500">
                            {money(taxAmount)}
                          </div>
                        </td>

                        <td className="px-4 py-3 text-right font-semibold">
                          {money(item.total_amount)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ============================================================
            PAYMENT SCHEDULE
        ============================================================ */}
        <div className="mt-8 border-t pt-6">
          <SectionTitle>Payment Schedule</SectionTitle>

          {paymentSchedules.length === 0 ? (
            <EmptyState message="No payment schedule available." />
          ) : (
            <div className="mt-3 overflow-x-auto rounded-lg border">
              <table className="w-full min-w-[760px] text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <TableHeader align="left">
                      #
                    </TableHeader>

                    <TableHeader align="left">
                      Payment
                    </TableHeader>

                    <TableHeader align="right">
                      Amount
                    </TableHeader>

                    <TableHeader align="right">
                      Due Date
                    </TableHeader>

                    <TableHeader align="right">
                      Paid
                    </TableHeader>

                    <TableHeader align="right">
                      Status
                    </TableHeader>
                  </tr>
                </thead>

                <tbody>
                  {paymentSchedules.map((schedule) => (
                    <tr
                      key={schedule.id}
                      className="border-b last:border-0"
                    >
                      <td className="px-4 py-3">
                        {schedule.sequence}
                      </td>

                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">
                          {schedule.label || "Payment"}
                        </p>
                      </td>

                      <td className="px-4 py-3 text-right">
                        {money(schedule.amount)}
                      </td>

                      <td className="px-4 py-3 text-right">
                        {formatDate(schedule.due_date)}
                      </td>

                      <td className="px-4 py-3 text-right">
                        {money(schedule.paid_amount)}
                      </td>

                      <td className="px-4 py-3 text-right">
                        <StatusBadge status={schedule.status} />

                        {schedule.paid_on && (
                          <p className="mt-1 text-xs text-gray-500">
                            Paid on{" "}
                            {formatDate(schedule.paid_on)}
                          </p>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div  className="mt-8 border-t pt-6">
          <PaymentTransactionsSection orderId={data.id} />

        </div>
      </div>
    </section>
  );
}

/* ============================================================
   SMALL UI COMPONENTS
============================================================ */

function SectionTitle({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <h2 className="text-sm font-semibold text-gray-900">
      {children}
    </h2>
  );
}

function EmptyState({
  message,
}: {
  message: string;
}) {
  return (
    <div className="mt-3 rounded-lg border border-dashed px-4 py-8 text-center text-sm text-gray-500">
      {message}
    </div>
  );
}

function TableHeader({
  children,
  align = "left",
}: {
  children: React.ReactNode;
  align?: "left" | "right";
}) {
  return (
    <th
      className={[
        "px-4 py-3 font-medium text-gray-600",
        align === "right" ? "text-right" : "text-left",
      ].join(" ")}
    >
      {children}
    </th>
  );
}

function StatusBadge({
  status,
}: {
  status?: string | null;
}) {
  const normalized = String(status ?? "")
    .trim()
    .toLowerCase();

  const config =
    normalized === "paid"
      ? {
          icon: CheckCircle2,
          className:
            "bg-green-50 text-green-700 ring-green-600/20",
        }
      : normalized === "pending"
        ? {
            icon: Clock3,
            className:
              "bg-amber-50 text-amber-700 ring-amber-600/20",
          }
        : {
            icon: AlertCircle,
            className:
              "bg-gray-50 text-gray-700 ring-gray-600/20",
          };

  const Icon = config.icon;

  return (
    <span
      className={[
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1",
        "text-xs font-medium capitalize ring-1 ring-inset",
        config.className,
      ].join(" ")}
    >
      <Icon size={13} />
      {status || "—"}
    </span>
  );
}

/* ============================================================
   SUMMARY
============================================================ */

function SummaryRow({
  label,
  value,
  strong = false,
}: {
  label: string;
  value?: string | number | null;
  strong?: boolean;
}) {
  return (
    <div
      className={
        strong
          ? "flex items-center justify-between text-base font-semibold"
          : "flex items-center justify-between"
      }
    >
      <span
        className={
          strong
            ? "text-gray-900"
            : "text-gray-600"
        }
      >
        {label}
      </span>

      <span className="text-gray-900">
        {money(value)}
      </span>
    </div>
  );
}

/* ============================================================
   FORMATTERS
============================================================ */

function money(
  value?: string | number | null,
) {
  const amount = Number(value ?? 0);

  if (!Number.isFinite(amount)) {
    return "₹0.00";
  }

  return `₹${amount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatPercentage(
  value?: string | number | null,
) {
  const percentage = Number(value ?? 0);

  if (!Number.isFinite(percentage)) {
    return "0%";
  }

  return `${percentage}%`;
}

function formatDate(
  value?: string | null,
) {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}
